<template>
  <div class="signup-wrapper">
    <div class="signup-content">
      <div class="signup-step-back">
        <v-icon color="#333333" size="22" @click="$router.go(-1)">
          mdi-arrow-left
        </v-icon>
      </div>
      <h2 class="signup-title">
        {{ $i18n.t("signup.identification.title") }}
      </h2>

      <v-form
        ref="form"
        v-model="valid"
        @submit.prevent="saveUserInfo"
        class="signup-form"
      >
        <v-text-field
          id="name"
          v-model="name"
          outlined
          rounded
          :hide-details="
            name !== null && !$refs.name.isFocused ? $refs.name.validate() : ''
          "
          required
          autocomplete="off"
          :placeholder="$i18n.t('signup.identification.personal.fields.name.placeholder')"
          :rules="nameRules"
          class="signup-input-field"
          ref="name"
        />
        <v-text-field
          id="father-family-name"
          v-model="fatherFamilyName"
          outlined
          rounded
          required
          autocomplete="off"
          :hide-details="
            fatherFamilyName !== null && !$refs.fatherFamilyName.isFocused
              ? $refs.fatherFamilyName.validate()
              : ''
          "
          :placeholder="
            $i18n.t(
              'signup.identification.personal.fields.father_family_name.placeholder',
            )
          "
          :rules="nameRules"
          class="signup-input-field"
          ref="fatherFamilyName"
        />
        <v-text-field
          id="mother-family-name"
          v-model="motherFamilyName"
          outlined
          rounded
          :hide-details="
            motherFamilyName !== null && !$refs.motherFamilyName.isFocused
              ? $refs.motherFamilyName.validate()
              : ''
          "
          required
          autocomplete="off"
          :placeholder="
            $i18n.t(
              'signup.identification.personal.fields.mother_family_name.placeholder',
            )
          "
          :rules="nameRules"
          class="signup-input-field"
          ref="motherFamilyName"
        />
        <v-autocomplete
          id="gender"
          v-model="gender"
          item-text="sex"
          item-value="code"
          outlined
          rounded
          required
          hide-details
          append-icon="mdi-chevron-down"
          auto-select-first
          autocomplete="off"
          :no-data-text="$i18n.t('base.noResults')"
          :items="availableGenders"
          :rules="genderRules"
          :placeholder="
            $i18n.t('signup.identification.personal.fields.gender.placeholder')
          "
          class="signup-input-field signup-autocomplete"
          :menu-props="{
            rounded: 'lg',
            contentClass: 'signup-custom-menu elevation-0',
            offsetY: true,
          }"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content :id="`gender-${item.code}`" class="dropdown-row">
              <v-list-item-title v-text="item.sex" class="signup-dropdown-item-text" />
            </v-list-item-content>
          </template>
        </v-autocomplete>

        <div class="signup-grid-fields">
          <div class="signup-birthdate-inputs">
            <div class="signup-form-label">
              {{ $i18n.t("signup.identification.personal.fields.birthday.label") }}
            </div>
            <div class="signup-coupled-inputs">
              <v-text-field
                id="birthdate-input-1"
                v-model="day"
                outlined
                rounded
                class="datefield birthday-field-left"
                :placeholder="
                  $i18n.t(
                    'signup.identification.personal.fields.birthday.day.placeholder',
                  )
                "
                required
                autocomplete="off"
                maxlength="2"
                hide-details
                oninput="this.value=this.value.slice(0,this.maxLength)"
                type="number"
                @input="tabNextField(day, 1, $event)"
              />

              <v-text-field
                id="birthdate-input-2"
                v-model="month"
                outlined
                rounded
                class="datefield birthday-field-middle"
                :placeholder="
                  $i18n.t(
                    'signup.identification.personal.fields.birthday.month.placeholder',
                  )
                "
                required
                autocomplete="off"
                maxlength="2"
                oninput="this.value=this.value.slice(0,this.maxLength)"
                type="number"
                hide-details
                @input="tabNextField(month, 2, $event)"
              />

              <v-text-field
                id="birthdate-input-3"
                v-model="year"
                outlined
                rounded
                class="datefield birthday-field-right"
                :placeholder="
                  $i18n.t(
                    'signup.identification.personal.fields.birthday.year.placeholder',
                  )
                "
                required
                autocomplete="off"
                maxlength="4"
                oninput="this.value=this.value.slice(0,this.maxLength)"
                type="number"
                hide-details
              />
            </div>

            <p id="invalid-date-alert" class="signup-error-message" v-if="isChildren || !validDate">
              {{ $i18n.t("signup.shared.errors.birthday") }}
            </p>
          </div>

          <div>
            <div class="signup-form-label">
              {{ $i18n.t("signup.identification.personal.fields.nationality.label") }}
            </div>
            <v-autocomplete
              id="nationality"
              v-model="nationality"
              :items="countries"
              item-text="country"
              item-value="code"
              outlined
              rounded
              required
              append-icon="mdi-chevron-down"
              auto-select-first
              autocomplete="off"
              :rules="countryRules"
              hide-details
              :no-data-text="$i18n.t('base.noResults')"
              :menu-props="{
                rounded: 'lg',
                contentClass: 'signup-custom-menu elevation-0',
                offsetY: true,
              }"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content class="dropdown-row">
                  <v-list-item-title
                    v-text="item.country"
                    class="signup-dropdown-item-text"
                  />
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </div>

          <div>
            <div class="signup-form-label">
              {{ $i18n.t("signup.identification.personal.fields.birth_country.label") }}
            </div>
            <v-autocomplete
              id="birth-country"
              v-model="birthCountry"
              :items="countries"
              item-text="country"
              item-value="code"
              outlined
              rounded
              required
              append-icon="mdi-chevron-down"
              auto-select-first
              autocomplete="off"
              :no-data-text="$i18n.t('base.noResults')"
              :rules="countryRules"
              hide-details
              :menu-props="{
                rounded: 'lg',
                contentClass: 'signup-custom-menu elevation-0',
                offsetY: true,
              }"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content class="dropdown-row">
                  <v-list-item-title
                    v-text="item.country"
                    class="signup-dropdown-item-text"
                  />
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </div>

          <div>
            <div class="signup-form-label">
              {{ $i18n.t("signup.identification.personal.fields.marital_status.label") }}
            </div>
            <v-autocomplete
              id="marital-status"
              v-model="maritalStatus"
              :items="availableMaritalStatus"
              append-icon="mdi-chevron-down"
              auto-select-first
              :no-data-text="$i18n.t('base.noResults')"
              item-text="status"
              item-value="code"
              outlined
              rounded
              required
              autocomplete="off"
              :rules="maritalRules"
              hide-details
              :menu-props="{
                rounded: 'lg',
                contentClass: 'signup-custom-menu elevation-0',
                offsetY: true,
              }"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content
                  :id="`maritalStatus-${item.code}`"
                  class="dropdown-row"
                >
                  <v-list-item-title
                    v-text="item.status"
                    class="signup-dropdown-item-text"
                  />
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </div>
        </div>

        <NewButton
          id="next-step"
          class="signup-step-button"
          :text="$i18n.t('signup.shared.buttons.next')"
          :loading="loading"
          :disabled="!valid || !validDate || loading || isChildren"
          :type="'submit'"
        />

        <div
          class="signup-cancel-container"
          @click="$router.push({ name: 'signupDashboard' })"
        >
          <p class="signup-cancel">
            {{ $i18n.t("base.cancel") }}
          </p>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import userSelects from "../../../mixins/userSelects.js";
import { signupFunctions } from "../../../mixins/signup.js";
import NewButton from "../../../components/shared/NewButton.vue";

dayjs.extend(customParseFormat);

export default {
  name: "PersonalView",

  components: { NewButton },

  mixins: [userSelects, signupFunctions],

  data() {
    return {
      valid: false,
      loading: false,
      // Values
      name: null,
      fatherFamilyName: null,
      motherFamilyName: null,
      gender: null,
      day: null,
      month: null,
      year: null,
      nationality: "CHL",
      birthCountry: "CHL",
      maritalStatus: 1,
      nameRules: [v => !!v || this.$i18n.t("signup.shared.errors.mustComplete")],
      genderRules: [
        v => !!v || this.$i18n.t("signup.shared.errors.mustComplete"),
        v => this.availableGenders.some(el => el.code === v) ||
          this.$i18n.t("signup.shared.errors.generic"),
      ],
      countryRules: [
        v => !!v || this.$i18n.t("signup.shared.errors.mustComplete"),
        v => this.countries.some(el => el.code === v) ||
          this.$i18n.t("signup.personal.errors.country.valid"),
      ],
      maritalRules: [
        v => !!v || this.$i18n.t("signup.shared.errors.mustComplete"),
        v => this.availableMaritalStatus.some(el => el.code === v) ||
          this.$i18n.t("signup.personal.errors.generic"),
      ],
    };
  },

  computed: {
    ...mapGetters(["signupIdentificationData"]),

    hasSpouse() {
      return this.maritalStatus === 2 || this.maritalStatus === 5;
    },

    birthday() {
      if (this.year && this.month && this.day) {
        return `${this.year}-${this.month.padStart(2, "0")}-${this.day.padStart(2, "0")}`;
      }

      return null;
    },

    isChildren() {
      if (!this.birthday) return false;
      const age = this.$options.filters.yearsOld(this.birthday);
      return age < 18;
    },

    validDate() {
      if (this.year && this.month && this.day) {
        return dayjs(this.birthday, "YYYY-MM-DD", true).isValid();
      }
      return false;
    },
  },

  methods: {
    ...mapActions(["setPersonal"]),

    async saveUserInfo() {
      this.loading = true;
      this.setPersonal({
        name: this.name,
        fatherFamilyName: this.fatherFamilyName,
        motherFamilyName: this.motherFamilyName,
        familyName: `${this.fatherFamilyName} ${this.motherFamilyName}`,
        gender: this.gender,
        birthday: this.birthday,
        birthCountry: this.birthCountry,
        nationality: this.nationality,
        maritalStatus: this.maritalStatus,
      });

      this.loading = false;
      this.$intercom.trackEvent(this.$i18n.t("signup.intercom.personal.event"), {
        status_registro: this.$i18n.t("signup.intercom.personal.message"),
      });

      if (this.hasSpouse) {
        return this.$router.push({ name: "signupIdentificationSpouse" });
      }
      return this.$router.push({ name: "signupIdentificationContact" });
    },

    tabNextField(dateValue, currentId, event) {
      if (["deleteContentBackward"].includes(event.inputType)) return;
      if (dateValue.toString().length === 2) {
        const nextInput = document.getElementById(`birthdate-input-${currentId + 1}`);
        // if we are in the last input box, the element will be null.
        if (nextInput === null) return;
        nextInput.focus();
      }
    },

    reloadData() {
      if (this.signupIdentificationData.name) {
        this.name = this.signupIdentificationData.name;
      }
      if (this.signupIdentificationData.fatherFamilyName) {
        this.fatherFamilyName = this.signupIdentificationData.fatherFamilyName;
      }
      if (this.signupIdentificationData.motherFamilyName) {
        this.motherFamilyName = this.signupIdentificationData.motherFamilyName;
      }
      if (this.signupIdentificationData.gender) {
        this.gender = this.signupIdentificationData.gender;
      }
      if (this.signupIdentificationData.birthday) {
        const dateObject = new Date(this.signupIdentificationData.birthday);
        this.year = dateObject.getFullYear().toString();
        this.month = (dateObject.getMonth() + 1).toString();
        this.day = (dateObject.getDate() + 1).toString();
      }
      if (this.signupIdentificationData.maritalStatus) {
        this.maritalStatus = this.signupIdentificationData.maritalStatus;
      }
      if (this.signupIdentificationData.birthCountry) {
        this.birthCountry = this.signupIdentificationData.birthCountry;
      }
      if (this.signupIdentificationData.nacionality) {
        this.nationality = this.signupIdentificationData.nacionality;
      }
    },
  },

  mounted() {
    this.reloadData();
  },
};
</script>

<style lang="scss" scoped>
.birthday-field-left {
  border-radius: 10px 0px 0px 10px !important;
}
.birthday-field-middle {
  border-radius: 0px 0px 0px 0px !important;
}
.birthday-field-right {
  border-radius: 0px 10px 10px 0px !important;
  min-width: 80px;
}

.signup-coupled-inputs {
  display: flex;
}

.signup-coupled-inputs > * {
  min-width: 65px;
}

.v-autocomplete__content.v-menu__content {
  box-shadow: none !important;
}

.signup-grid-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 10px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}
</style>
